import type { TUser } from "@/shared/services";
import { useAnalyticsStoreShallow } from "@pages/analytics/store";
import { closeDialog } from "@store/dialogs";
import { useAppDispatch } from "@store/store";
import { useCallback, useMemo, useState } from "react";
import { AudienceOverview } from "../audience-overview";
import { Devices } from "../devices";
import { TrafficType } from "../traffic-type";
import { EngagementFunnel } from "../engagement-funnel";
import { PerformanceOverview } from "../performance-overview";
import { ReadersOverview } from "../readers-overview";
import { SessionsTable } from "../sessions-table";
import { VersionDetailsHeader } from "./version-details-header";
import { VersionFilters } from "./version-filters";

const DIALOG_ID = "version-details";

export const VersionDetails = ({
  version_title,
  page_title,
  version_id,
  story_id,
  created_at,
  owner,
  isOwner,
}: {
  version_title: string;
  page_title: string;
  version_id: string;
  story_id: string;
  created_at: string;
  owner: TUser;
  isOwner: boolean;
}) => {
  const dispatch = useAppDispatch();
  const date = useAnalyticsStoreShallow((s) => s.date);
  const [overrideDate, setOverrideDate] = useState<{
    dateFrom: string;
    dateTo: string;
    index: number;
  }>(date);
  const close = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const overrides = useMemo(
    () => ({
      storyId: story_id,
      versionId: version_id,
      dateFrom: overrideDate.dateFrom,
      dateTo: overrideDate.dateTo,
    }),
    [story_id, version_id, overrideDate],
  );

  return (
    <div className="tw-p-6 -xl:tw-px-2 -xl:tw-py-4">
      <div className="tw-mx-auto tw-flex tw-w-[1106px] tw-flex-col tw-gap-6 tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-xl -xl:tw-w-full -xl:tw-rounded-lg -xl:tw-px-4 -xl:tw-pb-4 -xl:tw-pt-6 -lg:tw-gap-4">
        <VersionDetailsHeader
          versionName={version_title}
          storyName={page_title}
          createdAt={created_at}
          owner={owner}
          onClose={close}
        />
        <VersionFilters
          overrideDate={overrideDate}
          setOverrideDate={setOverrideDate}
        />
        {/* Two across, two rows. "Are these views real?" is asked about one
            version, not about an org - so the split belongs here, next to the
            device mix. Four cards in a row cramped every one of them: "Unique
            readers" wrapped to two lines, "Avg. reading time" to three, and both
            donut legends clipped their percentages at the card edge. */}
        <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-6 -xl:tw-mb-0 -xl:tw-gap-4 -md:tw-grid-cols-1">
          <AudienceOverview overrides={overrides} />
          <EngagementFunnel overrides={overrides} />
          {/* Devices defaults to spanning two columns below 2xl - correct in the
              three-column row it was built for, wrong here, where it took the
              whole row and orphaned Traffic type. */}
          <Devices overrides={overrides} className="-2xl:tw-col-span-1" />
          <TrafficType overrides={overrides} />
        </div>
        <ReadersOverview overrides={overrides} view="version-details" />
        <SessionsTable overrides={overrides} isOwner={isOwner} />
        <PerformanceOverview overrides={overrides} />
      </div>
    </div>
  );
};

VersionDetails.id = DIALOG_ID;
